import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import OnboardingLayout from '../components/OnboardingLayout';
import VerifyEmailBanner from '../components/VerifyEmailBanner';
import { Button } from '../components/ui/button';
import { User, Heart, Users, UsersRound, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const OPTIONS = [
  { value: 'just_me', label: 'Just me', icon: User },
  { value: 'with_partner', label: 'Me and my partner or spouse', icon: Heart },
  { value: 'with_friends', label: "Me and friends I already know", icon: Users },
  { value: 'open_to_roommates', label: "I'm open to finding roommates", icon: UsersRound },
];

const OnboardingLivingSituation = () => {
  const navigate = useNavigate();
  const { user, loading, updateOnboarding } = useAuth();
  const [selected, setSelected] = useState(user?.living_situation || null);
  const [saving, setSaving] = useState(false);

  if (loading) return null;
  if (!user) {
    navigate('/signup', { replace: true });
    return null;
  }

  const finish = async () => {
    setSaving(true);
    try {
      await updateOnboarding({
        living_situation: selected || undefined,
        onboarding_completed: true,
      });
      navigate('/home');
    } catch (e) {
      toast.error("We couldn't save that. Try again in a moment.");
    } finally {
      setSaving(false);
    }
  };

  const skip = async () => {
    try {
      await updateOnboarding({ onboarding_completed: true });
    } catch (_) {}
    navigate('/home');
  };

  return (
    <>
      <VerifyEmailBanner />
      <OnboardingLayout
        step={5}
        title="Who will you be living with?"
        testId="onboarding-living-situation"
      >
        <div className="space-y-3">
          {OPTIONS.map((opt) => {
            const active = selected === opt.value;
            const Icon = opt.icon;
            return (
              <button
                key={opt.value}
                type="button"
                onClick={() => setSelected(opt.value)}
                className={`w-full text-left p-5 rounded-2xl border-2 transition-all flex items-center gap-4 ${
                  active
                    ? 'border-amber-500 bg-amber-50/60 shadow-soft'
                    : 'border-slate-200 bg-white hover:border-slate-300 hover:bg-slate-50'
                }`}
                data-testid={`living-situation-option-${opt.value}`}
              >
                <div
                  className={`w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 ${
                    active ? 'bg-amber-200 text-amber-800' : 'bg-slate-100 text-slate-600'
                  }`}
                >
                  <Icon className="h-5 w-5" />
                </div>
                <span className="font-medium text-slate-900 text-base">{opt.label}</span>
              </button>
            );
          })}
        </div>

        <div className="mt-8 space-y-3">
          <Button
            onClick={finish}
            disabled={saving}
            className="w-full h-12 btn-primary text-base"
            data-testid="living-situation-finish-btn"
          >
            {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Finish and see places
          </Button>
          <button
            onClick={skip}
            className="w-full text-sm text-slate-600 hover:text-slate-900 py-2"
            data-testid="living-situation-skip-link"
          >
            Skip this step
          </button>
        </div>
      </OnboardingLayout>
    </>
  );
};

export default OnboardingLivingSituation;
